import { Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight, Check, UserCheck, FolderTree, FileText, Settings } from 'lucide-react';

export function CoreHRPage() {
  const features = [
    { icon: UserCheck, title: 'Employee Database', description: 'Centralized employee records with custom fields and advanced search' },
    { icon: FolderTree, title: 'Org Hierarchy', description: 'Visual org charts with reporting structures and team management' },
    { icon: FileText, title: 'Document Management', description: 'Secure document storage with version control and e-signatures' },
    { icon: Settings, title: 'Custom Workflows', description: 'Configurable approval workflows for any HR process' },
  ];

  const businessImpact = [
    'Reduce data entry time by 80%',
    'Eliminate paper-based processes',
    'Improve data accuracy by 95%',
    'Enable self-service for employees',
  ];

  return (
    <>
      <SEO
        title="Core HR - Nestor"
        description="Complete employee lifecycle management with digital records, org charts, and document management. Streamline your HR operations with our Core HR module."
      />

      <section className="bg-gradient-to-b from-primary/5 via-background to-background py-24">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 text-center lg:text-left">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">Core HR</h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                The backbone of your organization. Manage digital records, org structures, and documents with an interface that requires
                <span className="text-primary font-semibold"> zero training</span>.
              </p>
              <Button size="lg" className="h-12 px-8 rounded-full" asChild>
                <Link to="/demo">Request Demo <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
            <div className="flex-1 w-full max-w-lg lg:max-w-xl">
              {/* Illustration Placeholder */}
              <div className="aspect-video bg-gradient-to-tr from-purple-100 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-3xl flex items-center justify-center shadow-sm border border-border/50">
                <span className="text-6xl">🗂️</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Key Features</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <Card key={index}>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <feature.icon className="h-5 w-5 text-primary" />
                      </div>
                      <CardTitle>{feature.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-8">Business Impact</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {businessImpact.map((impact, index) => (
                <div key={index} className="flex items-center gap-3 text-left">
                  <Check className="h-5 w-5 text-primary flex-shrink-0" />
                  <span>{impact}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
